import {
  Button,
  Heading,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Stack,
  Text,
  Tooltip,
  useDisclosure,
  useToast,
} from '@chakra-ui/react';
import { useQueryClient } from '@tanstack/react-query';
import { useUserContext } from 'context/UserContextProvider';
import { dateTransformerNoTimezone } from 'helpers/dateTransformer';
import { defaultToastOptions } from 'helpers/manageErrorResponse';
import { useCancelSubscriptionMutation } from 'hooks/mutations/useCancelSubscriptionMutation';
import useWorkshopQuery from 'hooks/useWorkshopQuery';
import { useMemo } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { FaFileExcel } from 'react-icons/fa';

import { DangerZoneRow } from './DangerZoneRow';

type Props = {
  workshopId: number;
};

export const EndContract = ({ workshopId }: Props) => {
  const toast = useToast(defaultToastOptions);
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const cancelSubscriptionMutation = useCancelSubscriptionMutation();
  const { isOpen, onOpen, onClose } = useDisclosure();
  const { user } = useUserContext();
  const workshopQuery = useWorkshopQuery(workshopId);

  const isInFreeTrial = workshopQuery.data?.in_free_trial;
  const subscriptionStatus = workshopQuery.data?.subscription?.status;
  const cancelledSubscription = subscriptionStatus == 300 && !isInFreeTrial;
  const subscriptionId = workshopQuery.data?.subscription?.id;

  const workshopActiveUntil = workshopQuery.data?.subscription?.ended_at;
  const activeSubscription = !cancelledSubscription && !isInFreeTrial;

  const termEnd = workshopQuery.data?.subscription?.current_term_end;

  const disabledTooltip = useMemo<string | null>(() => {
    if (!user.permissions.includes('workshops:cancel-subscription')) {
      return t('workshop:end_contract.tooltip.disabled.no_permission');
    }
    if (isInFreeTrial) {
      return t('workshop:end_contract.tooltip.disabled.free_trial');
    }
    if (subscriptionStatus !== 200) {
      return t('workshop:end_contract.tooltip.disabled.no_subscription');
    }
    return null;
  }, [isInFreeTrial, subscriptionStatus, t, user.permissions]);

  const workshopHasSpfUsers = workshopQuery.data?.users.some((user) => user.type === 'systempartner') ?? false;

  return (
    <>
      <DangerZoneRow>
        <Stack gap={0}>
          <Heading size="xxs" as="h3">
            {t('workshop:end_contract.title')}
          </Heading>
          <Text>
            {cancelledSubscription && (
              <Trans i18nKey="workshop:end_contract.contract_ended.body" values={{ date: dateTransformerNoTimezone(workshopActiveUntil) }} />
            )}
            {isInFreeTrial && t('workshop:end_contract.free_trial.body')}
            {activeSubscription && t('workshop:end_contract.default.body')}
          </Text>
        </Stack>
        {cancelledSubscription ? (
          <Button minWidth="24ch" disabled>
            {t('workshop:end_contract.contract_ended.button')}
          </Button>
        ) : (
          <Tooltip label={disabledTooltip} hasArrow>
            <Button
              leftIcon={<FaFileExcel />}
              minWidth="24ch"
              colorScheme="red"
              onClick={onOpen}
              isLoading={cancelSubscriptionMutation.isPending}
              loadingText={t('workshop:end_contract.default.button')}
              disabled={disabledTooltip != null || cancelSubscriptionMutation.isPending}
            >
              {t('workshop:end_contract.default.button')}
            </Button>
          </Tooltip>
        )}
      </DangerZoneRow>

      <Modal size="xl" isOpen={isOpen} onClose={onClose}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>{t('workshop:end_contract.modal.title')}</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <Text>
              <Trans i18nKey="workshop:end_contract.modal.body.part_1" values={{ date: termEnd ? dateFormatter.format(new Date(termEnd)) : '' }} />
            </Text>
            <Text pb="6">
              {workshopHasSpfUsers ? (
                <Trans i18nKey="workshop:end_contract.modal.body.part_2_with_spf_user" />
              ) : (
                <Trans i18nKey="workshop:end_contract.modal.body.part_2_no_spf_user" />
              )}
            </Text>
          </ModalBody>

          <ModalFooter>
            <Button mr={3} onClick={onClose}>
              {t('workshop:end_contract.modal.button_secondary')}
            </Button>
            <Button
              variant="solid"
              colorScheme="red"
              onClick={() => {
                cancelSubscriptionMutation.mutate(
                  {
                    subscriptionId: subscriptionId!,
                  },
                  {
                    onSuccess: () => {
                      onClose();
                      queryClient.invalidateQueries({ queryKey: useWorkshopQuery.getKey(workshopId) });
                      toast({
                        title: t('workshop:end_contract.toast.success.title'),
                        status: 'success',
                      });
                    },
                    onError: (error) => {
                      toast({
                        title: error.message ?? t('errors:general'),
                        status: 'error',
                      });
                    },
                  },
                );
              }}
              isLoading={cancelSubscriptionMutation.isPending}
              loadingText={t('workshop:end_contract.modal.button_primary')}
            >
              {t('workshop:end_contract.modal.button_primary')}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </>
  );
};

const dateFormatter = Intl.DateTimeFormat('de-AT', { day: '2-digit', month: '2-digit', year: '2-digit', hour: '2-digit', minute: 'numeric' });
